import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { soundManager } from "@/lib/soundManager";
import {
  AlertCircle,
  BarChart3,
  Brain,
  MessageSquare,
  TrendingUp,
  Zap,
  Copy,
  Trash2,
  Check,
  Send,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Streamdown } from "streamdown";
import { toast } from "sonner";

type Tab = "stats" | "chat";

export default function Insights() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("stats");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<
    Array<{ role: "user" | "assistant"; content: string; timestamp: Date }>
  >([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, isChatLoading]);

  // Focus input when switching to chat tab
  useEffect(() => {
    if (activeTab === "chat") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [activeTab]);

  // Fetch AI learning insights
  const { data: insights, isLoading: insightsLoading } = trpc.aiLearning.getInsights.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Fetch method performance
  const { data: methodPerformance, isLoading: methodsLoading } = trpc.aiLearning.getMethodPerformance.useQuery(
    undefined,
    { enabled: isAuthenticated }
  );

  // Fetch method weights
  const { data: methodWeights, isLoading: weightsLoading } = trpc.aiLearning.getMethodWeights.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Chat mutation
  const chatMutation = trpc.aiLearning.chat.useMutation({
    onSuccess: (response: { message: string }) => {
      setChatMessages((prev) => [...prev, { role: "assistant", content: response.message, timestamp: new Date() }]);
      soundManager.playSuccess();
      setIsChatLoading(false);
    },
    onError: (error: { message: string }) => {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${error.message}`, timestamp: new Date() },
      ]);
      soundManager.playError();
      setIsChatLoading(false);
    },
  });

  const handleSendMessage = () => {
    if (!chatInput.trim() || isChatLoading) return;
    soundManager.playClick();
    const userMessage = chatInput.trim();
    // Capture current history BEFORE adding the new user message
    const historySnapshot = chatMessages.map((m) => ({ role: m.role, content: m.content }));
    setChatMessages((prev) => [...prev, { role: "user", content: userMessage, timestamp: new Date() }]);
    setChatInput("");
    setIsChatLoading(true);
    chatMutation.mutate({
      message: userMessage,
      history: historySnapshot,
    });
  };

  const handleCopyMessage = (content: string, index: number) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    soundManager.playSuccess();
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleClearChat = () => {
    setChatMessages([]);
    soundManager.playClick();
    toast.success("Chat cleared");
  };

  // ── Auth gates ──────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Brain className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-2">AI Insights Dashboard</h2>
          <p className="text-muted-foreground mb-6">
            Login to access AI performance analytics, method insights, and interactive chat with the prediction AI.
          </p>
          <Button asChild className="w-full">
            <a href={getLoginUrl()}>Login to View Insights</a>
          </Button>
        </Card>
      </div>
    );
  }

  const isLoading = insightsLoading || methodsLoading || weightsLoading;

  // ── CHAT TAB — full-screen, no outer scroll ─────────────────────────────────
  if (activeTab === "chat") {
    return (
      <div
        className="flex flex-col bg-slate-950"
        style={{ height: "100dvh", maxHeight: "100dvh" }}
      >
        {/* Chat header — fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-primary/20 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab("stats")}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-slate-800"
              aria-label="Back to stats"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm leading-tight">AI Assistant</p>
              <p className="text-xs text-green-400 leading-tight">
                {isChatLoading ? "Thinking..." : "Online"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {chatMessages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearChat}
                className="text-muted-foreground hover:text-red-400 h-8 w-8 p-0"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Messages area — fills remaining space, scrolls independently */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ overscrollBehavior: "contain" }}>
          {chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground px-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Brain className="w-8 h-8 text-primary/60" />
              </div>
              <p className="font-semibold text-foreground mb-1">Start a conversation</p>
              <p className="text-sm mb-6">Ask me anything about the lottery data</p>
              <div className="grid grid-cols-1 gap-2 w-full max-w-xs">
                {[
                  "How many times has 1077 appeared?",
                  "Show me the most frequent numbers",
                  "What are the recent draws?",
                  "Which numbers can I play tonight?",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setChatInput(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="text-left text-xs px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-primary/40 transition-all text-muted-foreground hover:text-foreground"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                      <Brain className="w-3.5 h-3.5 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-tr-sm"
                        : "bg-slate-800 text-foreground rounded-tl-sm"
                    }`}
                  >
                    <div className="text-sm leading-relaxed">
                      {msg.role === "assistant" ? (
                        <Streamdown>{msg.content}</Streamdown>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    <div className={`flex items-center mt-1 gap-2 ${msg.role === "user" ? "justify-end" : "justify-between"}`}>
                      <p className={`text-xs ${msg.role === "user" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                      {msg.role === "assistant" && (
                        <button
                          onClick={() => handleCopyMessage(msg.content, idx)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title="Copy"
                        >
                          {copiedIndex === idx ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isChatLoading && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                    <Brain className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="bg-slate-800 rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input bar — fixed at bottom, never scrolls away */}
        <div className="flex-shrink-0 px-3 py-3 bg-slate-900 border-t border-slate-800">
          <div className="flex items-end gap-2 max-w-2xl mx-auto">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Ask about numbers, patterns..."
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 text-sm placeholder:text-muted-foreground transition-all"
                disabled={isChatLoading}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || isChatLoading}
              className="w-11 h-11 rounded-full bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 transition-all active:scale-95"
              aria-label="Send message"
            >
              <Send className="w-4 h-4 text-primary-foreground" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STATS TAB ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-20 md:pb-8">
      <Navigation />

      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 border-b border-primary/20">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-7 h-7 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">AI Insights</h1>
                <p className="text-sm text-muted-foreground">Performance analytics & AI chat</p>
              </div>
            </div>
            {/* Chat CTA button */}
            <button
              onClick={() => setActiveTab("chat")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 hover:border-primary/50 transition-all text-sm font-medium text-primary"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">AI Chat</span>
              {chatMessages.length > 0 && (
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {chatMessages.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="container py-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading AI insights...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall Stats */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">Overall Performance</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-2xl font-bold text-primary">{insights?.methodPerformance?.length || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">AI Methods</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-2xl font-bold text-green-500">{insights?.overallStats?.totalEvaluated || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Evaluated</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-500">
                    {insights?.overallStats?.avgAccuracy ? `${insights.overallStats.avgAccuracy}%` : "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Avg Accuracy</p>
                </div>
                <div className="text-center p-4 bg-slate-800/50 rounded-xl">
                  <p className="text-2xl font-bold text-purple-500">{insights?.overallStats?.totalMatches || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Matches</p>
                </div>
              </div>
            </Card>

            {/* Method Performance */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">Method Performance</h2>
              </div>
              {methodPerformance && methodPerformance.length > 0 ? (
                <div className="space-y-4">
                  {methodPerformance.map((method) => (
                    <div key={method.methodName} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{method.methodName}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{method.totalPredictions} predictions</span>
                          <span className="text-sm font-bold text-primary">{method.accuracyRate.toFixed(2)}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-primary h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(method.accuracyRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No method performance data yet</p>
                  <p className="text-xs mt-1">Generate predictions and wait for draws to see AI learning in action</p>
                </div>
              )}
            </Card>

            {/* Method Weights */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">Method Weights (AI Learning)</h2>
              </div>
              {methodWeights && Object.keys(methodWeights).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(methodWeights).map(([methodName, weight]) => {
                    const isHot = weight > 100;
                    const isCold = weight < 100;
                    return (
                      <div
                        key={methodName}
                        className={`p-4 rounded-xl border-2 ${
                          isHot
                            ? "bg-red-500/10 border-red-500/50"
                            : isCold
                            ? "bg-blue-500/10 border-blue-500/50"
                            : "bg-slate-800/50 border-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{methodName}</span>
                          {isHot && <span className="text-xs text-red-500 font-bold">🔥 HOT</span>}
                          {isCold && <span className="text-xs text-blue-500 font-bold">❄️ COLD</span>}
                        </div>
                        <p className="text-2xl font-bold">{weight.toFixed(0)}%</p>
                        <p className="text-xs text-muted-foreground">
                          {isHot ? "Performing above average" : isCold ? "Performing below average" : "Baseline performance"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No method weights data yet</p>
                </div>
              )}
            </Card>

            {/* Chat entry card */}
            <button
              onClick={() => setActiveTab("chat")}
              className="w-full p-5 rounded-2xl bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/20 hover:border-primary/40 transition-all text-left group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Chat with AI Assistant</p>
                    <p className="text-sm text-muted-foreground">
                      {chatMessages.length > 0
                        ? `${chatMessages.length} message${chatMessages.length > 1 ? "s" : ""} — tap to continue`
                        : "Ask about numbers, patterns, and statistics"}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
